import type { Job, JobError, JobProgress, JobRun, Trigger } from "@/lib/jobs";

import { ScheduleEditor, Switch } from "./ScheduleEditor";
import { SettingsEditor } from "./SettingsEditor";
import { TallyCard, Tile } from "./TallyCard";
import {
  duration,
  elapsedLabel,
  jobsSharingTime,
  lastRunLabel,
  nextColumn,
  percent,
  queuedDetail,
  runSummary,
  timestamp,
  triggerLabel, verdictWord } from "./schedule";

/**
 * The phone job detail — `6j2` scheduled, `6j3` running, `6j4` failed.
 *
 * One screen with three faces rather than three screens: **when the job ends, this card *is* the
 * result and nothing in the layout moves.** The running tally is the completion report being
 * written live.
 */
export function JobDetail({
  job,
  jobs,
  live,
  runs,
  errors,
  onBack,
  onRun,
  onCancel,
  onRetry,
  onSaveTrigger,
  onSaveSettings,
  onError,
  onOpenCard,
}: {
  job: Job;
  /** The whole list, for the schedule editor's two hints — both are questions about the set. */
  jobs: Job[];
  live: JobProgress | null;
  runs: JobRun[];
  errors: JobError[];
  onBack: () => void;
  onRun: () => void;
  onCancel: () => void;
  onRetry: () => void;
  onSaveTrigger: (trigger: Trigger) => Promise<unknown>;
  /** Partial: only the keys that changed. Rejected values throw, and the field snaps back. */
  onSaveSettings: (values: Record<string, number>) => Promise<unknown>;
  onError: (error: unknown) => void;
  onOpenCard: (noteId: number) => void;
}) {
  const running = job.state === "running";
  const queued = job.state === "queued";
  const failed = job.state === "failed";

  return (
    <>
      <header className="flex h-[50px] shrink-0 items-center gap-2.5 border-b border-line bg-panel px-3.5">
        <button type="button" onClick={onBack} aria-label="設定へ戻る" className="text-ink-2">
          ‹
        </button>
        <span className="flex-1 text-[16px]/[1] font-semibold text-ink">{job.name}</span>
        <span
          className={[
            "size-2 rounded-full",
            running
              ? "bg-accent"
              : queued
                ? "border-[1.5px] border-accent-line"
                : failed
                  ? "bg-accent"
                  : job.state === "passed"
                    ? "bg-ink-3"
                    : job.lastRun
                      ? "bg-ink-off"
                      : "border border-line-strong",
          ].join(" ")}
        />
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-[11px] overflow-auto p-3.5">
        {running ? (
          <RunningCard job={job} live={live} onCancel={onCancel} />
        ) : queued ? (
          <QueuedCard job={job} onCancel={onCancel} />
        ) : failed && job.lastRun ? (
          <FailedCard
            job={job}
            errors={errors}
            onRetry={onRetry}
            onRun={onRun}
            onOpenCard={onOpenCard}
          />
        ) : (
          <HeaderCard job={job} onRun={onRun} />
        )}

        {!running && (
          <>
            <Card label="スケジュール">
              <ScheduleEditor
                key={job.id}
                phone
                trigger={job.trigger}
                blocked={job.blocked}
                sharingTime={jobsSharingTime(jobs, job)}
                onSave={onSaveTrigger}
                onError={onError}
              />
            </Card>

            {job.settings.length > 0 && (
              // No card at all when a job declares none — an empty Settings card would be a
              // control that has never done anything, which is the state `registry.py` argues
              // against for jobs themselves.
              <Card label="設定">
                <SettingsEditor
                  key={job.id}
                  phone
                  settings={job.settings}
                  values={job.settingValues}
                  onSave={onSaveSettings}
                  onError={onError}
                />
              </Card>
            )}

            <Card>
              <div className="flex min-h-[52px] items-center gap-[11px] px-[13px] py-[11px]">
                <span className="flex-1 text-[16px]/[1.3] text-ink">失敗時に通知</span>
                <Switch
                  phone
                  label="失敗時に通知"
                  on={job.trigger.notifyOnFailure}
                  onClick={() =>
                    void Promise.resolve(
                      onSaveTrigger({
                        ...job.trigger,
                        notifyOnFailure: !job.trigger.notifyOnFailure,
                      }),
                    ).catch(onError)
                  }
                />
              </div>
            </Card>
          </>
        )}

        <Card label="最近の実行">
          {runs.length === 0 ? (
            <p className="px-[13px] py-3 text-secondary text-ink-4">まだありません。</p>
          ) : (
            runs.map((run) => (
              <div
                key={run.id}
                className="flex items-center gap-2.5 border-b border-line-soft px-[13px] py-2.5 last:border-b-0"
              >
                <span className="w-[104px] shrink-0 font-mono text-secondary text-ink-2">
                  {timestamp(run.startedAt)}
                </span>
                <span className={`text-secondary ${verdictTone(run.verdict)}`}>
                  {verdictWord(run.verdict)}
                </span>
                <span className="min-w-0 flex-1 text-right text-meta text-ink-4">
                  {runSummary(run)}
                </span>
              </div>
            ))
          )}
        </Card>
      </div>
    </>
  );
}

/** Cancelled is ink-3, not red — nothing went wrong and nothing was lost. */
function verdictTone(verdict: string): string {
  if (verdict === "failed") return "text-accent-text font-semibold";
  if (verdict === "passed") return "text-ink-3";
  return "text-ink-3";
}

function Card({ label, children }: { label?: string; children: React.ReactNode }) {
  return (
    <section className="shrink-0 overflow-hidden rounded-[var(--r-3)] border border-line bg-panel">
      {label && (
        <div className="border-b border-line-soft px-[13px] py-[9px]">
          <span className="text-field-label font-semibold uppercase tracking-[.1em] text-ink-3">
            {label}
          </span>
        </div>
      )}
      {children}
    </section>
  );
}

function HeaderCard({ job, onRun }: { job: Job; onRun: () => void }) {
  return (
    <section className="flex shrink-0 flex-col gap-[11px] rounded-[var(--r-3)] border border-line bg-panel p-[13px]">
      <p className="text-[15px]/[1.5] text-ink-2 text-pretty">{job.description}.</p>
      <div className="flex items-center gap-2.5">
        <span className="flex flex-1 flex-col gap-[3px]">
          <span
            className={`text-[15px]/[1.3] font-semibold ${verdictTone(job.lastRun?.verdict ?? "idle")}`}
          >
            {lastRunLabel(job.lastRun)}
          </span>
          {job.lastRun && (
            // The TALLY CARD below carries the numbers, so this line carries only what it cannot
            // — the filename, the fields added, why it stopped — and falls back to the duration.
            // `runSummary` here would print the same counts twice, a hand's width apart.
            <span className="font-mono text-meta text-ink-4">
              {job.lastRun.detail || duration(job.lastRun.durationMs)}
            </span>
          )}
        </span>
        {/* Keyed on the trigger rather than on `nextRunAt`, which is null for a manual job and
            non-null for a blocked one — the question here is "does this have a schedule at all",
            and `nextColumn` answers what that schedule is currently worth. */}
        {job.trigger.mode !== "manual" && (
          <span className="flex flex-col gap-[3px] text-right">
            <span className="text-field-label font-semibold uppercase tracking-[.05em] text-ink-4">
              次回
            </span>
            <span
              className={`text-secondary ${job.blocked ? "font-semibold text-accent-text" : "text-ink-2"}`}
            >
              {triggerLabel(job.trigger)} · {nextColumn(job)}
            </span>
          </span>
        )}
      </div>
      {/* `6j3`'s This-run card, kept after the run ends: when the job finishes this IS the
          result and nothing in the layout moves. */}
      <TallyCard run={job.lastRun} />
      <button
        type="button"
        onClick={onRun}
        className="min-h-[48px] rounded-[var(--r-3)] bg-accent text-[16px]/[1] font-semibold text-white"
      >
        今すぐ実行
      </button>
    </section>
  );
}

/**
 * `6j3`'s shape for a job that has not started yet — 日本-307.
 *
 * Deliberately quiet: a place in line, what it is waiting for, and a way out. No progress bar
 * because there is no progress, and **no estimated start time** — see `queuedDetail`.
 */
function QueuedCard({ job, onCancel }: { job: Job; onCancel: () => void }) {
  return (
    <section className="flex shrink-0 flex-col gap-[13px] rounded-[var(--r-3)] border border-line bg-panel px-[13px] py-[15px]">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[30px]/[1] font-semibold text-ink">
          {job.queuePosition ?? "—"}
        </span>
        <span className="text-[17px]/[1.3] text-ink-3">
          {job.queuePosition === 1 ? "待機 · 先頭" : "待機中"}
        </span>
      </div>
      <span className="font-mono text-meta text-ink-4">{queuedDetail(job)}</span>
      <button
        type="button"
        onClick={onCancel}
        className="min-h-[48px] rounded-[var(--r-3)] border border-line-strong bg-panel text-[16px]/[1] font-semibold text-ink-2"
      >
        中止
      </button>
      {/* One expression rather than two wrapped text lines: JSX joins an interior newline with a
          space, which would read as `スケジュールは そのままです`. */}
      <p className="text-meta text-ink-4 text-pretty">
        {"前のジョブが終わると自動で始まります。中止してもこの実行が消えるだけで、スケジュールはそのままです。"}
      </p>
    </section>
  );
}

function RunningCard({
  job,
  live,
  onCancel,
}: {
  job: Job;
  live: JobProgress | null;
  onCancel: () => void;
}) {
  const current = live?.current ?? 0;
  const total = live?.total ?? 0;
  return (
    <section className="flex shrink-0 flex-col gap-[13px] rounded-[var(--r-3)] border border-line bg-panel px-[13px] py-[15px]">
      {/* Count first, percentage second: 412 of 1,204 is what tells you whether to wait. */}
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[30px]/[1] font-semibold text-ink">
          {current.toLocaleString()}
        </span>
        <span className="font-mono text-[17px]/[1] text-ink-4">/ {total.toLocaleString()}</span>
        <span className="flex-1" />
        <span className="font-mono text-[17px]/[1] font-semibold text-accent-text">
          {percent(current, total)}%
        </span>
      </div>
      <span className="h-[7px] overflow-hidden rounded-[var(--r-2)] bg-line-soft">
        <span
          className="block h-full rounded-[var(--r-2)] bg-accent"
          style={{ width: `${percent(current, total)}%` }}
        />
      </span>
      <div className="flex items-center gap-2.5">
        <span className="font-jp text-[21px]/[1] text-ink">{live?.item ?? ""}</span>
        <span className="flex-1 text-secondary text-ink-3">{live?.message ?? "処理中"}</span>
        <span className="font-mono text-meta text-ink-4">
          {live ? elapsedLabel(live.elapsedSeconds, live.current, live.total) : ""}
        </span>
      </div>
      <button
        type="button"
        onClick={onCancel}
        className="min-h-[48px] rounded-[var(--r-3)] border border-line-strong bg-panel text-[16px]/[1] font-semibold text-ink-2"
      >
        中止
      </button>
      <p className="text-meta text-ink-4 text-pretty">
        この画面を離れても実行は続きます。 {job.cancelNote}
      </p>
    </section>
  );
}

function FailedCard({
  job,
  errors,
  onRetry,
  onRun,
  onOpenCard,
}: {
  job: Job;
  errors: JobError[];
  onRetry: () => void;
  onRun: () => void;
  onOpenCard: (noteId: number) => void;
}) {
  const run = job.lastRun!;
  const shown = errors.slice(0, 3);
  const rest = run.errorCount - shown.length;

  return (
    <>
      <section className="flex shrink-0 flex-col gap-3 rounded-[var(--r-3)] border border-accent-border bg-accent-ground px-[13px] py-[14px]">
        <div className="flex items-baseline gap-[9px]">
          <span className="text-[17px]/[1.3] font-semibold text-accent-text">{verdictWord("failed")}</span>
          <span className="text-secondary text-ink-3">
            {timestamp(run.startedAt)} · {duration(run.durationMs)}
          </span>
        </div>
        <div className="flex gap-[9px]">
          <Tile value={run.changed.toLocaleString()} label="修正" tone="text-ink-3" />
          <Tile value={run.errorCount.toLocaleString()} label="エラー" tone="text-accent-text" />
        </div>
        {/* The run's own numbers under the verdict pair — a failed run still did most of its
            work, and `Needs review 17` says something `errors` does not. */}
        <TallyCard run={run} />
        {/* Say what survived before saying what refused. */}
        <p className="text-secondary text-ink-2 text-pretty">
          成功したカードは保存されました。下の{run.errorCount.toLocaleString()}
          件はそのままです。
        </p>
        <div className="flex gap-[9px]">
          {job.retryable && (
            <button
              type="button"
              onClick={onRetry}
              className="min-h-[48px] flex-1 rounded-[var(--r-3)] bg-accent text-[16px]/[1] font-semibold text-white"
            >
              失敗した{run.errorCount.toLocaleString()}件を再試行
            </button>
          )}
          <button
            type="button"
            onClick={onRun}
            className="min-h-[48px] flex-1 rounded-[var(--r-3)] border border-line-strong bg-panel text-[16px]/[1] font-semibold text-ink-2"
          >
            全件実行
          </button>
        </div>
      </section>

      {shown.length > 0 && (
        <Card label="エラー">
          {shown.map((error, i) => (
            <button
              key={`${error.noteId}-${i}`}
              type="button"
              onClick={() => error.noteId !== null && onOpenCard(error.noteId)}
              className="flex min-h-[56px] w-full items-center gap-[11px] border-b border-line-soft px-[13px] py-[11px] text-left last:border-b-0"
            >
              <span className="w-[52px] shrink-0 font-jp text-[20px]/[1.3] text-ink">
                {error.expression}
              </span>
              <span className="min-w-0 flex-1 text-secondary text-ink-2">{error.reason}</span>
              <span className="shrink-0 text-[16px]/[1] text-ink-4">›</span>
            </button>
          ))}
          {rest > 0 && (
            <p className="py-3 text-center text-secondary text-ink-3">
              {rest.toLocaleString()} more not shown
            </p>
          )}
        </Card>
      )}
    </>
  );
}

